import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { storeAuthData } from '@/utils/auth';
import { 
  trackAccountCreation,
  trackAuthFlow,
  trackCheckoutFlowCompleted,
  trackCheckoutError,
  ERROR_CATEGORIES 
} from '@/utils/checkout-analytics';
import { trackCategorizedError } from '@/utils/error-categorization';
import { handleAccountCreationError } from '@/utils/error-handling';
import { useAccountCreationRetry } from '@/hooks/useRetryHandler.jsx';
import { AccountCreationErrorDisplay } from './ErrorDisplay';
import { AccountCreationRetryInterface } from './RetryInterface';
import { backgroundJobSystem } from '@/utils/retry-mechanisms';
import { 
  trackAccountSetupTime,
  trackCompleteFlowTime 
} from '@/utils/analytics-dashboard';
import { 
  recordFunnelStep,
  recordFunnelError,
  completeFunnelSession,
  trackAccountCreationSuccess,
  trackAccountCreationFailure,
  trackTimeToCompletion,
  FUNNEL_STEPS,
  ERROR_STEPS 
} from '@/utils/conversion-funnel';

/**
 * AccountSetupFlow Component
 * Handles post-payment account setup and authentication
 */
export default function AccountSetupFlow({ 
  stripeCustomerId, 
  email, 
  phoneNumber, 
  onComplete 
}) {
  const [setupStatus, setSetupStatus] = useState('initializing');
  const [setupStartTime, setSetupStartTime] = useState(null);
  const [accountError, setAccountError] = useState(null);
  const navigate = useNavigate();

  // Initialize account creation retry handler
  const accountRetry = useAccountCreationRetry({
    onSuccess: (result) => {
      handleAccountCreationSuccess(result);
    },
    onError: (error) => {
      setAccountError(error);
      handleAccountCreationFailure(error);
    },
    maxUserRetries: 3
  });

  useEffect(() => {
    if (stripeCustomerId && email && phoneNumber) {
      setSetupStartTime(new Date());
      initiateAccountSetup();
    }
  }, [stripeCustomerId, email, phoneNumber]);

  const initiateAccountSetup = async () => {
    setSetupStatus('creating_account');
    setAccountError(null);

    // Create account creation function for retry mechanism
    const createAccountOperation = async () => {
      const response = await fetch('/api/auth/create-account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          stripeCustomerId,
          email,
          phoneNumber,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Account creation failed');
      }

      return data;
    };

    try {
      // Execute with retry mechanism
      const result = await accountRetry.executeWithRetry(createAccountOperation, {
        stripeCustomerId: stripeCustomerId,
        email: email,
        phone_number: phoneNumber
      });

      if (result.success) {
        handleAccountCreationSuccess(result);
      } else {
        throw new Error(result.error || 'Account setup failed');
      }

    } catch (error) {
      // Handle error with comprehensive error processing
      const processedError = handleAccountCreationError(error, {
        stripeCustomerId: stripeCustomerId,
        email: email,
        phone_number: phoneNumber,
        setup_time: setupStartTime ? Date.now() - setupStartTime.getTime() : null
      });

      // Queue for background processing if it's a retryable error
      if (processedError.canRetry) {
        backgroundJobSystem.queueFailedAccountSetup({
          stripeCustomerId,
          email,
          phoneNumber
        }, error);
      }

      setAccountError(processedError);
      setSetupStatus('failed');
    }
  };

  const handleAccountCreationSuccess = (data) => {
    // Track successful account creation
    trackAccountCreation('success', {
      stripeCustomerId: stripeCustomerId,
      email: email,
      has_twilio_setup: !!data.verificationSid,
    });
    
    trackAccountCreationSuccess({
      stripeCustomerId: stripeCustomerId,
      email: email,
      setup_time: setupStartTime ? Date.now() - setupStartTime.getTime() : null,
    });
        
        setSetupStatus('establishing_session');
        
        // Track auth flow start
        trackAuthFlow('started', {
          email: email,
          has_verification_sid: !!data.verificationSid,
        });
        
        recordFunnelStep(FUNNEL_STEPS.AUTH_COMPLETED, {
          email: email,
          has_auth_token: !!data.authToken,
        });
        
        // Store authentication data if we have the necessary information
        if (data.verificationSid && data.authToken) {
          storeAuthData(
            data.verificationSid,
            email,
            phoneNumber,
            data.authToken,
            72 // 72 hours expiration
          );
          
          setSetupStatus('completed');
          
          // Track successful auth flow
          trackAuthFlow('success', {
            email: email,
            auth_token_received: true,
          });
          
          // Track complete checkout flow completion
          const totalTime = setupStartTime ? Date.now() - setupStartTime.getTime() : null;
          
          trackCheckoutFlowCompleted({
            email: email,
            stripeCustomerId: stripeCustomerId,
            total_completion_time: totalTime,
          });
          
          trackTimeToCompletion(totalTime, {
            email: email,
            stripeCustomerId: stripeCustomerId,
          });
          
          // Track account setup time
          if (totalTime) {
            trackAccountSetupTime(totalTime, {
              email: email,
              stripeCustomerId: stripeCustomerId,
              twilio_setup_success: !!data.verificationSid,
            });
            
            trackCompleteFlowTime(totalTime, {
              email: email,
              stripeCustomerId: stripeCustomerId,
              stepsCompleted: 10, // Assuming all steps completed
              errorCount: 0,
            });
          }
          
          // Complete funnel session
          completeFunnelSession({
            email: email,
            stripeCustomerId: stripeCustomerId,
            total_time: totalTime,
            success: true,
          });
          
          // Call completion callback
          if (onComplete) {
            onComplete(data.authToken);
          }
          
          // Redirect to checkout completion route which will redirect to home
          // This ensures proper route protection is applied
          setTimeout(() => {
            navigate('/checkout/complete', { replace: true });
          }, 1500);
          
    } else {
      // Account created but no immediate session - user needs to log in manually
      setSetupStatus('manual_login_required');
      
      trackAuthFlow('failed', {
        email: email,
        reason: 'no_verification_sid_or_token',
      });
    }
  };

  const handleAccountCreationFailure = (error) => {
    // Track account creation failure
    trackAccountCreation('failed', {
      stripeCustomerId: stripeCustomerId,
      email: email,
      error_message: error.originalError?.message || error.userMessage?.message,
      error_category: error.category,
    });
    
    trackAccountCreationFailure({
      stripeCustomerId: stripeCustomerId,
      email: email,
      error_message: error.originalError?.message || error.userMessage?.message,
      error_category: error.category,
    });
    
    recordFunnelError(ERROR_STEPS.ACCOUNT_CREATION_FAILED, {
      error_message: error.originalError?.message || error.userMessage?.message,
      error_category: error.category,
      stripeCustomerId: stripeCustomerId,
    });
    
    trackCheckoutError(error.originalError || new Error(error.userMessage?.message), 'account_setup', ERROR_CATEGORIES.ACCOUNT_CREATION);
  };

  const handleManualRetry = async () => {
    setAccountError(null);
    accountRetry.resetRetryState();
    await initiateAccountSetup();
  };

  const handleSkipStep = () => {
    // Continue to manual login if account creation fails
    setSetupStatus('manual_login_required');
  };

  const handleContactSupport = () => {
    const supportReference = accountError?.supportReference || 'N/A';
    window.location.href = `mailto:support@lexi.tips?subject=Account Setup Issue&body=Support Reference: ${supportReference}`;
  };

  const handleManualLogin = () => {
    // Store email for login convenience
    sessionStorage.setItem('tempEmail', email);
    navigate('/login');
  };

  const renderStatusContent = () => {
    switch (setupStatus) {
      case 'initializing':
        return (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F0A7D6] mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold mb-2">Initializing Account Setup</h3>
            <p className="text-gray-600">Preparing your account...</p>
          </div>
        );

      case 'creating_account':
        return (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F0A7D6] mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold mb-2">Creating Your Account</h3>
            <p className="text-gray-600">Setting up your Lexi Tips account with Twilio authentication...</p>
          </div>
        );

      case 'establishing_session':
        return (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F0A7D6] mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold mb-2">Establishing Session</h3>
            <p className="text-gray-600">Logging you in automatically...</p>
          </div>
        );

      case 'retrying':
        return (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold mb-2">Retrying Setup</h3>
            <p className="text-gray-600">
              Attempt {retryCount + 1} of {maxRetries}... Please wait.
            </p>
          </div>
        );

      case 'completed':
        return (
          <div className="text-center">
            <div className="rounded-full h-12 w-12 bg-green-100 flex items-center justify-center mx-auto mb-4">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2 text-green-600">Setup Complete!</h3>
            <p className="text-gray-600">Redirecting you to your dashboard...</p>
          </div>
        );

      case 'manual_login_required':
        return (
          <div className="text-center">
            <div className="rounded-full h-12 w-12 bg-blue-100 flex items-center justify-center mx-auto mb-4">
              <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8V7a4 4 0 00-8 0v4h8z"></path>
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">Account Created Successfully</h3>
            <p className="text-gray-600 mb-4">
              Your account has been created, but you'll need to log in manually to complete the setup.
            </p>
            <button
              onClick={handleManualLogin}
              className="bg-[#F0A7D6] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#e391c4] transition-colors"
            >
              Continue to Login
            </button>
          </div>
        );

      case 'failed':
        return (
          <div className="space-y-4">
            {accountError ? (
              <AccountCreationRetryInterface
                retryState={accountRetry}
                onRetry={handleManualRetry}
                onSkipStep={handleSkipStep}
                onContactSupport={handleContactSupport}
                stepName="account setup"
              />
            ) : (
              <div className="text-center">
                <div className="rounded-full h-12 w-12 bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2 text-red-600">Setup Failed</h3>
                <p className="text-gray-600 mb-4">
                  There was an error setting up your account. Please try again or contact support.
                </p>
                <div className="space-y-2">
                  <button
                    onClick={handleManualRetry}
                    className="bg-[#F0A7D6] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#e391c4] transition-colors mr-2"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={handleManualLogin}
                    className="bg-gray-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-gray-600 transition-colors"
                  >
                    Manual Login
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        {renderStatusContent()}
        
        {/* Progress indicator */}
        <div className="mt-6">
          <div className="flex justify-between text-xs text-gray-500 mb-2">
            <span>Account Setup</span>
            <span>
              {setupStatus === 'completed' ? '100%' : 
               setupStatus === 'establishing_session' ? '75%' :
               setupStatus === 'creating_account' ? '50%' : '25%'}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-500 ${
                setupStatus === 'completed' ? 'bg-green-500 w-full' :
                setupStatus === 'establishing_session' ? 'bg-[#F0A7D6] w-3/4' :
                setupStatus === 'creating_account' ? 'bg-[#F0A7D6] w-1/2' :
                setupStatus === 'failed' ? 'bg-red-500 w-1/4' :
                'bg-[#F0A7D6] w-1/4'
              }`}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
}